import { api } from './client'

/**
 * F-068 (TASK-0322, REQ-238-001 / REQ-239-001 / REQ-240-001): importazione CSV di lead e clienti.
 *
 * Il flusso è lo stesso per i due tipi: anteprima (non scrive nulla) -> applicazione (una sola volta,
 * crea il lotto durevole) -> eventuale annullamento dell'intero lotto, possibile solo se nessun dato
 * dipende dai suoi record. Endpoint: `/api/leads/imports`, `/api/customers/imports` e `/api/imports`
 * (storico unificato, dipendenze, annullamento — annullare richiede il ruolo Admin).
 */
export type ImportKind = 'leads' | 'customers'

export const IMPORT_KIND_LABELS: Record<ImportKind, string> = {
  leads: 'Lead',
  customers: 'Clienti',
}

export interface ImportMappableField {
  key: string
  label: string
  dataType: string
}

export interface ImportMappableFields {
  fields: ImportMappableField[]
  requiredRule?: string
  requiredContactChannelRule?: string
}

export type ImportRowOutcome = 'Importable' | 'MissingRequiredField' | 'InvalidValue' | 'Duplicate'

export interface ImportDuplicateReason {
  field: string
  where: string
  existingId?: number | null
  otherRow?: number | null
}

export interface ImportPreviewRow {
  rowNumber: number
  outcome: ImportRowOutcome
  problems: string[]
  problemMessages?: string[]
  /** Solo clienti: duplicati nel file o già presenti. */
  duplicates?: ImportDuplicateReason[]
  /** Solo clienti: avvisi non bloccanti (telefono già noto). */
  warnings?: ImportDuplicateReason[]
  values: Record<string, unknown>
}

export interface ImportPreview {
  importId: string
  createdAt: string
  fileName?: string | null
  totalRowsInFile: number
  processedRowCount: number
  truncated: boolean
  maxRowsPerImport: number
  /** Testo dell'intestazione del file -> chiave del campo. */
  appliedMapping: Record<string, string>
  byOutcome: Partial<Record<ImportRowOutcome, number>>
  applied: boolean
  rows: ImportPreviewRow[]
}

export interface ImportApplyResult {
  importId: string
  batchId: string
  createdLeadCount?: number
  createdCustomerCount?: number
}

export interface ImportBatch {
  kind: ImportKind
  id: string
  fileName: string | null
  createdBy: number | null
  createdAt: string
  appliedAt: string
  totalRowsInFile: number
  importedCount: number
  duplicateCount: number
  invalidCount: number
  status: 'Applicato' | 'Annullato'
  undoneAt: string | null
  undoneBy: number | null
  undoReason: string | null
}

export interface ImportBatchDependencies {
  kind: ImportKind
  id: string
  status: string
  recordCount: number
  canUndo: boolean
  totalDependencies: number
  dependencies: { area: string; count: number }[]
}

const BASE: Record<ImportKind, string> = {
  leads: '/api/leads/imports',
  customers: '/api/customers/imports',
}

export const importsApi = {
  mappableFields: (kind: ImportKind) => api<ImportMappableFields>(`${BASE[kind]}/mappable-fields`),

  preview: (kind: ImportKind, csv: string, mapping: Record<string, string> | null, fileName: string | null) =>
    api<ImportPreview>(BASE[kind], {
      method: 'POST',
      body: JSON.stringify({ csv, mapping, fileName }),
    }),

  apply: (kind: ImportKind, importId: string) =>
    api<ImportApplyResult>(`${BASE[kind]}/${importId}/apply`, { method: 'POST' }),

  listBatches: (filters: { kind?: ImportKind; status?: string } = {}) => {
    const q = new URLSearchParams()
    if (filters.kind) q.set('kind', filters.kind)
    if (filters.status) q.set('status', filters.status)
    const qs = q.toString()
    return api<{ total: number; items: ImportBatch[] }>(`/api/imports${qs ? `?${qs}` : ''}`)
  },

  dependencies: (kind: ImportKind, id: string) =>
    api<ImportBatchDependencies>(`/api/imports/${kind}/${id}/dependencies`),

  undo: (kind: ImportKind, id: string, reason: string) =>
    api<{ kind: ImportKind; id: string; status: string; deletedRecords: number }>(
      `/api/imports/${kind}/${id}/undo`,
      { method: 'POST', body: JSON.stringify({ reason }) },
    ),
}
